import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import PageHeader from '../../components/PageHeader';
import LoadingSpinner from '../../components/LoadingSpinner';
import EmptyState from '../../components/EmptyState';
import { billingApi } from '../../services/billing';

const statusStyles: Record<string, string> = {
  paid: 'bg-emerald-50 text-emerald-700',
  open: 'bg-amber-50 text-amber-700',
  void: 'bg-slate-100 text-slate-500',
  uncollectible: 'bg-red-50 text-red-700',
};

function formatCurrency(amount: number, currency: string): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency.toUpperCase(),
  }).format(amount);
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

export default function VendorBillingInvoices() {
  const { data: invoices, isLoading } = useQuery({
    queryKey: ['billing', 'invoices'],
    queryFn: () => billingApi.getInvoices().then((r) => r.data),
  });

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  const results = invoices?.results ?? [];

  return (
    <>
      <PageHeader
        title="Invoice History"
        actions={
          <Link
            to="/vendor/billing"
            className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border-2 border-slate-200 rounded-xl hover:bg-slate-50"
          >
            Back to Billing
          </Link>
        }
      />

      {results.length === 0 ? (
        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6">
          <EmptyState
            title="No invoices yet"
            description="Invoices will appear here once you have an active subscription."
            icon={
              <svg className="h-6 w-6 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
              </svg>
            }
          />
        </div>
      ) : (
        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-100">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-slate-400 uppercase">Date</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-slate-400 uppercase">Period</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-slate-400 uppercase">Amount</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-slate-400 uppercase">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-slate-400 uppercase">PDF</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {results.map((invoice) => (
                  <tr key={invoice.id} className="hover:bg-slate-50/50">
                    <td className="px-6 py-4 text-sm text-slate-900">
                      {formatDate(invoice.created_at)}
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-500">
                      {formatDate(invoice.period_start)} &ndash; {formatDate(invoice.period_end)}
                    </td>
                    <td className="px-6 py-4 text-sm font-medium text-slate-900">
                      {formatCurrency(invoice.amount, invoice.currency)}
                    </td>
                    <td className="px-6 py-4">
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold capitalize ${
                          statusStyles[invoice.status] ?? 'bg-slate-100 text-slate-500'
                        }`}
                      >
                        {invoice.status}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      {invoice.invoice_pdf_url ? (
                        <a
                          href={invoice.invoice_pdf_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm font-medium text-blue-600 hover:text-blue-700"
                        >
                          Download
                        </a>
                      ) : (
                        <span className="text-sm text-slate-300">&mdash;</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </>
  );
}
